(ns arthur.flow.regenerate
  "Recompute a changed feature from retained source tracks, then replace only
  channels owned by that feature. Upload remains project/save's ordinary job."
  (:require [arthur.domain.channel :as ch]
            [arthur.domain.clip :as clip]
            [arthur.domain.feature :as feature]
            [arthur.domain.node :as node]
            [arthur.domain.params :as params]
            [arthur.flow.address :as address]
            [arthur.flow.freeze :as freeze]
            [arthur.flow.take :as take]))

(defn- settings
  "One feature's MEASUREMENT inputs. The teeth read the mouth's aperture cut
  because `condition/interior` will not smooth a contour on a frame the mouth is
  shut on: an input edge between two features, and the only one there is."
  [clip fid]
  (let [f (get-in clip [:features fid])
        mouth (first (for [[id peer] (:features clip)
                           :when (and (= :mouth (:area peer))
                                      (= (:subject f) (:subject peer)))] id))]
    (cond-> (feature/effective-params clip fid)
      (and (= :teeth (:area f)) mouth)
      (assoc :aperture-cut (:aperture-cut (feature/effective-params clip mouth))))))

(defn- reads
  "The knob values one feature's frozen channels actually depend on.

  Narrower than its measurement inputs, and that difference IS the dirty-set
  calculation. `:contour-avg` is a subject setting every feature inherits and the
  teeth block does not read, so inheriting a knob and being stale because of it are
  not the same thing. `address/area-knobs` is what knows which is which, over the
  table `address-test` asserts by biconditional — so there is no second per-knob
  list here to drift away from the one that is checked."
  [clip fid]
  (select-keys (settings clip fid)
               (address/area-knobs (get-in clip [:features fid :area]))))

(defn- rebased
  "`fresh` in place of `old`, carrying `old`'s corrections across.

  THIS IS WHAT A LAYER IS FOR. The base is regenerated and the hand work is not,
  which is the whole reason a correction is stored over a channel rather than
  written into it. A correction the new base no longer fits is MARKED rather than
  dropped or misapplied — `channel/conflicts` is how a view finds it — and one
  that fits again has its mark cleared, because a regeneration that restores the
  topology has resolved it."
  [old fresh]
  (let [fresh (cond-> fresh (:repairs old) (assoc :repairs (:repairs old)))]
   (if-let [over (seq (:over old))]
    (assoc fresh :over (ch/reconcile (assoc fresh :over (vec over))))
    fresh)))

(defn- bases
  "A node's channels without their corrections.

  For asking whether the authored channels still ARE the measurement: a
  correction is not a hand PLACEMENT. The base under it has to go on following
  re-measurement, or the first correction anyone makes would freeze the part it
  was meant to adjust."
  [channels]
  (into {} (map (fn [[p c]] [p (dissoc c :over :repairs)])) channels))

(defn- replace-feature [entry fragment fid]
  (let [paths (for [id (get-in entry [:clip :features fid :nodes])
                    [prop channel] (get-in fragment [:nodes id :channels])
                    :when (:generated channel)]
                [id prop channel])]
    (-> (reduce (fn [entry [id prop channel]]
                  (let [at [:clip :symbols (get-in entry [:clip :features fid :symbol])
                            :nodes id :channels prop]
                        old (get-in entry at)]
                    (assoc-in entry at (rebased old channel))))
                entry paths)
        (update :store merge (:store fragment)))))

(defn plan
  "The changed document, the feature IDs an edit dirties, and the subject they
  belong to. Also reports tier-2 block roles from the address table for the
  debug UI."
  [clip {:keys [scope id knob value]}]
  (let [area (get-in params/definitions [knob :area])
        collection (case scope
                     :subject :subjects :feature :features :group :groups
                     (throw (ex-info "unknown setting scope" {:scope scope})))
        owner (get-in clip [collection id])]
    (when-not (and owner (params/valid-value? knob value)
                   (case scope
                     :subject (= area :subject)
                     :feature (= area (:area owner))
                     :group (and (= area :eye) (= :eye-pair (:kind owner)))))
      (throw (ex-info "invalid scoped setting" {:scope scope :id id
                                                :knob knob :value value})))
    (let [changed (assoc-in clip [collection id :params knob] value)
          subject (if (= scope :subject) id (:subject owner))
          ;; Every feature of the subject is a candidate, not just the edited
          ;; object's own members, because a knob can reach a feature it does not
          ;; belong to: `:aperture-cut` is a mouth setting that the TEETH read.
          ;; `reads` is what narrows this back down, and it is the only thing
          ;; that does — no per-knob cases here, in either direction.
          candidates (sort-by str (for [[fid f] (:features clip)
                                        :when (= subject (:subject f))] fid))]
      {:changed changed
       :subject subject
       :features (vec (filter #(not= (reads clip %) (reads changed %)) candidates))
       :roles (address/invalidates knob)})))

(defn- regenerate-feature
  "One dirty feature, re-measured through the shared anchor and re-frozen. A brow
  reads the eye corners and the teeth read mouth aperture; `take/measure-part`
  owns those input edges, and neither one is a request to freeze the other
  feature."
  [base-params base source-inputs entry fid]
  (let [{:keys [area subject]} (get-in entry [:clip :features fid])
        params (merge base-params (settings (:clip entry) fid))]
    (when (and (= :teeth area) (nil? (:interior source-inputs)))
      (throw (ex-info "teeth regeneration needs retained pixel measurements"
                      {:feature fid})))
    (replace-feature entry
                     (freeze/part subject area params
                                  (take/measure-part area params source-inputs @base))
                     fid)))

(defn- regenerate-head
  "Re-freeze ONE SUBJECT's head transform, which is a different job from a
  feature's: its only input is that subject's conditioned anchor, and it owns no
  channels to replace. The authored `:channels` follow the measurement while they
  still ARE the measurement, and are left alone once somebody has placed the head
  by hand."
  [entry params base subject]
  ;; The face's footage is registered by the same fit, inverted, so it is
  ;; re-frozen with the head whenever the face has one — see `freeze/plate-part`.
  (let [plate (get-in entry [:clip :symbols subject :nodes :plate])
        footage (some->> plate node/source (clip/symbol (:clip entry)))
        params (cond-> params
                 plate (assoc :footage {:height (:height footage)}))]
    (reduce
     (fn [entry [id bake]]
       (let [baked (bake subject params @base)
             at [:clip :symbols subject :nodes id]
             old (get-in entry at)
             measured (:measured baked)]
         (cond-> (-> entry
                     (assoc-in (conj at :measured) measured)
                     (update :store merge (:store baked)))
           (= (bases (:channels old)) (bases (:measured old)))
           (assoc-in (conj at :channels)
                     (into {} (map (fn [[p c]] [p (rebased (get-in old [:channels p]) c)]))
                           measured)))))
     entry
     (cond-> [[:head freeze/head-part]]
       plate (conj [:plate freeze/plate-part])))))

(defn change
  "One scoped static edit. `source-inputs` holds dense landmarks and, when the
  teeth are dirty, retained pixel measurements. No IO or app-db here."
  [{:keys [clip source-inputs] :as entry} edit]
  (let [{:keys [changed subject features]} (plan clip edit)
        analysis-id (get-in changed [:subjects subject :analysis])
        analysis (get-in changed [:analyses analysis-id])
        _ (when-not analysis
            (throw (ex-info "subject names a missing analysis"
                            {:subject subject :analysis analysis-id})))
        ;; THE EDITED SUBJECT'S OWN LANDMARKS. Retained source is per subject —
        ;; one video, one dense track per tracked face — so re-measuring the
        ;; second face through the first one's anchor is the mistake this lookup
        ;; exists to prevent.
        source-inputs (get-in source-inputs [:subjects subject])
        _ (when-not (:dense source-inputs)
            (throw (ex-info "regeneration needs retained source landmarks"
                            {:subject subject})))
        base-params (merge take/knobs
                           {:fps (or (:fps analysis) (:fps changed))
                            :aspect (:aspect analysis)
                            :analysis analysis})
        ;; One conditioned anchor for the whole edit, and it is the SUBJECT's.
        ;; `:anchor-avg` is a subject setting, so the shared upstream measurement
        ;; is not read off whichever dirty feature happened to sort first — and
        ;; the head below does not need a feature to exist at all.
        anchor-params (merge base-params (params/for-area :subject)
                             (get-in changed [:subjects subject :params]))
        base (delay (take/anchor-base anchor-params source-inputs))]
    (cond-> (reduce (partial regenerate-feature base-params base source-inputs)
                    (assoc entry :clip changed) features)
      (= :anchor-avg (:knob edit)) (regenerate-head anchor-params base subject))))
